/**
 * Register Page Component
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Mail, Shield, ChevronRight, ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { AppBrandLogo } from '../components/AppBrandLogo';
import AuthSplitShell from '../components/marketing/AuthSplitShell';
import { IMAGES } from '../lib/marketingImages';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import CountdownTimer from '../components/CountdownTimer';
import { API_URL, ALLOW_DEV_OTP, OTP_TIMEOUT_SECONDS } from '../config';
import { applyOtpPaste, handleOtpPasteEvent } from '../lib/otpUtils';
import { GoogleOAuthSection } from '../components/OAuthButtons';
import { useNoIndex } from '../lib/useNoIndex';

const RegisterPage = () => {
  useNoIndex();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ identifier: "", fullName: "", pin: "", confirmPin: "" });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [devOtp, setDevOtp] = useState(null);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resending, setResending] = useState(false);
  const [otpTimeout, setOtpTimeout] = useState(OTP_TIMEOUT_SECONDS);
  const [otpSession, setOtpSession] = useState(0); // bump to restart the countdown
  const [registrationIdentifier, setRegistrationIdentifier] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleOAuthSuccess = (data) => {
    const userData = login(data.access_token, data.user);
    toast.success("Account ready!");
    if (data.needs_pin_setup || userData?.needs_pin_setup) {
      navigate("/setup-pin");
    } else {
      navigate(userData?.is_admin ? "/admin" : "/");
    }
  };

  const isEmail = formData.identifier.includes("@");

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.identifier.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    if (!isEmail) {
      toast.error("Please register with your email address. SMS verification is not available yet.");
      return;
    }
    
    if (!formData.fullName.trim() || formData.fullName.length < 2) {
      toast.error("Please enter your full name");
      return;
    }
    
    if (!formData.pin || formData.pin.length < 4 || formData.pin.length > 6) {
      toast.error("PIN must be 4-6 digits");
      return;
    }
    
    if (formData.pin !== formData.confirmPin) {
      toast.error("PINs do not match");
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        full_name: formData.fullName,
        pin: formData.pin,
        email: formData.identifier.trim().toLowerCase(),
      };
      
      const response = await axios.post(`${API_URL}/auth/register`, requestData);
      if (response.data.email_sent === false && !response.data.dev_otp) {
        toast.error("We couldn't send your verification email. Please try again or contact support.");
        return;
      }
      toast.success("Verification code sent! Check your email (and spam folder).");
      if (response.data.dev_otp) setDevOtp(response.data.dev_otp);
      if (response.data.expires_in_seconds) setOtpTimeout(response.data.expires_in_seconds);
      setRegistrationIdentifier(response.data.identifier || formData.identifier.trim().toLowerCase());
      setOtpSession((s) => s + 1);
      setOtpExpired(false);
      setStep(2);
    } catch (error) {
      const detail = error.response?.data?.detail || "Registration failed";
      toast.error(detail);
      if (typeof detail === "string" && detail.toLowerCase().includes("already exists")) {
        setTimeout(() => navigate("/login"), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResending(true);
    try {
      const response = await axios.post(`${API_URL}/auth/resend-otp?identifier=${encodeURIComponent(registrationIdentifier || formData.identifier)}`);
      toast.success("New verification code sent!");
      if (response.data.dev_otp) setDevOtp(response.data.dev_otp);
      if (response.data.expires_in_seconds) setOtpTimeout(response.data.expires_in_seconds);
      setOtpSession((s) => s + 1);
      setOtpExpired(false);
      setOtp(["", "", "", "", "", ""]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend code");
    } finally {
      setResending(false);
    }
  };

  const handleOtpExpire = () => {
    setOtpExpired(true);
    toast.error("Verification code expired! Please request a new one.");
  };

  const handleOtpChange = (index, value) => {
    const { otp: newOtp, focusIndex } = applyOtpPaste(otp, index, value);
    setOtp(newOtp);
    if (newOtp[index] !== otp[index] || value.replace(/\D/g, "").length > 1) {
      document.getElementById(`otp-${focusIndex}`)?.focus();
    }
  };

  const handleOtpPaste = (index, e) => {
    const { otp: newOtp, focusIndex } = handleOtpPasteEvent(e, otp, index);
    setOtp(newOtp);
    document.getElementById(`otp-${focusIndex}`)?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter the complete verification code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/auth/verify-registration`, { 
        identifier: registrationIdentifier || formData.identifier, 
        code 
      });
      login(response.data.access_token, response.data.user);
      toast.success("Account created successfully!");
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthSplitShell
      image={IMAGES.phonePay}
      kicker="Join free"
      headline="Create an account. Pay your first bill before the kettle boils."
      caption="Households across Malawi use SmartPay to keep lights, water, and TV going."
    >
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full">
        <div className="text-center mb-6 lg:text-left">
          <div className="lg:hidden"><AppBrandLogo banner className="mb-4" /></div>
          <h1 className="text-3xl font-bold text-foreground">Create your home account</h1>
          <p className="text-muted-foreground mt-2">No queues. No paperwork. Just bills that get paid.</p>
        </div>

        <div className="bg-card border border-border/50 rounded-2xl p-8 shadow-xl" data-testid="register-card">
          {step === 1 ? (
            <>
              <GoogleOAuthSection onSuccess={handleOAuthSuccess} disabled={loading} />
            <form onSubmit={handleRegister} className="space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2 text-muted-foreground">Full Name</label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input type="text" value={formData.fullName} onChange={(e) => setFormData(p => ({ ...p, fullName: e.target.value }))}
                    placeholder="John Doe" className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    data-testid="fullname-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-muted-foreground">Email address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input type="email" value={formData.identifier} onChange={(e) => setFormData(p => ({ ...p, identifier: e.target.value }))}
                    placeholder="you@example.com" className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                    data-testid="identifier-input" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">We send your verification code by email. You can add a phone number later in your profile.</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-muted-foreground">Create PIN</label>
                  <div className="relative">
                    <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input type="password" inputMode="numeric" maxLength={6} value={formData.pin} 
                      onChange={(e) => setFormData(p => ({ ...p, pin: e.target.value.replace(/\D/g, "") }))}
                      placeholder="4-6 digits" className="w-full pl-12 pr-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all tracking-widest"
                      data-testid="pin-input" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-muted-foreground">Confirm PIN</label>
                  <input type="password" inputMode="numeric" maxLength={6} value={formData.confirmPin}
                    onChange={(e) => setFormData(p => ({ ...p, confirmPin: e.target.value.replace(/\D/g, "") }))}
                    placeholder="Repeat PIN" className="w-full px-4 py-4 bg-input/50 border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all tracking-widest"
                    data-testid="confirm-pin-input" />
                </div>
              </div>

              <button type="submit" disabled={loading} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg" data-testid="register-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Create Account <ChevronRight className="w-5 h-5" /></>}
              </button>

              <button type="button" onClick={() => navigate("/login")} className="w-full py-3 text-muted-foreground hover:text-foreground transition-all flex items-center justify-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Already have an account? Login
              </button>
            </form>
            </>
          ) : (
            <form onSubmit={handleVerify} className="space-y-5">
              <button type="button" onClick={() => setStep(1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>

              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Verify Your Email</h3>
                <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to {registrationIdentifier || formData.identifier}</p>
              </div>

              <div className="p-4 bg-muted/50 rounded-xl">
                <CountdownTimer key={otpSession} seconds={otpTimeout} onExpire={handleOtpExpire} label="Code expires in" />
              </div>

              {ALLOW_DEV_OTP && devOtp && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                  <p className="text-xs text-amber-600 dark:text-amber-400 text-center">Dev Mode - OTP: <span className="font-mono font-bold">{devOtp}</span></p>
                </div>
              )}

              <div className="flex justify-center gap-2">
                {otp.map((digit, i) => (
                  <input key={i} id={`otp-${i}`} type="text" inputMode="numeric" maxLength={6} value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value.replace(/\D/g, ""))}
                    onPaste={(e) => handleOtpPaste(i, e)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold bg-input border border-border rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={otpExpired}
                    data-testid={`otp-input-${i}`} />
                ))}
              </div>

              {otpExpired && (
                <div className="text-center">
                  <p className="text-sm text-red-500 mb-2">Code expired!</p>
                  <button type="button" onClick={handleResendOtp} disabled={resending}
                    className="text-primary hover:underline flex items-center justify-center gap-2 mx-auto">
                    {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Resend Code
                  </button>
                </div>
              )}

              <button type="submit" disabled={loading || otpExpired} className="w-full py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg" data-testid="verify-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify & Complete <ChevronRight className="w-5 h-5" /></>}
              </button>

              {!otpExpired && (
                <button type="button" onClick={handleResendOtp} disabled={resending}
                  className="w-full py-2 text-muted-foreground hover:text-foreground flex items-center justify-center gap-2">
                  {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Didn't receive code? Resend
                </button>
              )}
            </form>
          )}
        </div>
      </motion.div>
    </AuthSplitShell>
  );
};

export default RegisterPage;